/**
 * Phase 328 wave 5 task 5.2 — the SCHEDULER behind draft auto-save (AC-5).
 *
 * Saving on every keystroke would be one PUT per character; saving on blur would lose the
 * answer a subject is mid-way through when their phone rings. What the card asks for sits
 * between the two, and is three rules rather than one:
 *
 *  1. DEBOUNCE — rapid changes collapse into a single write carrying the LAST value.
 *  2. SINGLE FLIGHT — never two writes on the wire at once. The endpoint keeps exactly one row
 *     per token and upserts it wholesale, so two concurrent PUTs that land out of order leave
 *     the STALE answers stored. This is the rule that makes auto-save safe rather than merely
 *     quiet.
 *  3. COALESCE — a change made while a write is in flight is not dropped: it re-arms once that
 *     write settles, so the newest answers always get their turn.
 *
 * Phase 425 — failure is REPORTED, not thrown, and the three rules above are untouched. A
 * failed write is still never raised at the caller (there is no error path to force on a
 * subject who did not ask for any of this, and nothing accumulates to be lost — the whole
 * answer set is sent every time), but every write's outcome is handed to the optional
 * {@link SaveStatus} observer, so the page can say "Saving…" / "Saved 14:32" / "we couldn't
 * save" instead of quietly stopping. The retry is still simply the next change — or a
 * {@link Autosave.flush}, which is what an explicit "Save progress" control is.
 *
 * It is deliberately framework-free and value-generic: no React, no knowledge of forms, tokens
 * or HTTP. That keeps the timing rules testable as timing rules — and keeps the page holding
 * one object instead of four refs and a hand-rolled timer.
 */

/**
 * The debounce window. Long enough that typing a sentence is one save; short enough that a
 * subject who closes the tab mid-thought loses at most a phrase. The card's bar is "~1-2s".
 */
export const DRAFT_DEBOUNCE_MS = 1200;

/**
 * What became of the most recent write.
 *
 * `at` is `Date.now()` at the moment the write SETTLED, not when it started: the caller renders
 * a clock from it ("Saved 14:32"), and the honest time to show a subject is when their answers
 * actually reached the server. The formatting lives with the caller — this file holds no
 * opinion about locales.
 */
export type SaveStatus =
  | { kind: "saving" }
  | { kind: "saved"; at: number }
  | { kind: "failed"; at: number };

export interface Autosave<T> {
  /** Records `value` as the one to write, and (re)starts the debounce window. */
  schedule(value: T): void;
  /**
   * Writes NOW instead of at the end of the window — an explicit "save my progress", where the
   * subject is waiting for the answer rather than being kept out of the way.
   *
   * `value`, when given, is recorded as the pending one first (so the write always carries what
   * is on screen at the click, not what the last debounce happened to capture); with no
   * argument it flushes whatever the window was already holding. The three timing rules still
   * hold: if a write is in flight this one queues BEHIND it (rule 2) and then writes the newest
   * pending value exactly once (rule 1) rather than adding a second PUT to the wire.
   *
   * Resolves once the resulting write has settled — or immediately when there is nothing to
   * write. It never rejects: the outcome arrives through the status observer like any other
   * write's.
   */
  flush(value?: T): Promise<void>;
  /**
   * Drops a pending write and disarms the timer. An in-flight write is NOT aborted — it is
   * already server-side work — but nothing will follow it. Call on unmount, and once the form
   * is submitted (the server deletes the draft inside the submit unit of work, so a queued
   * save has nothing left to say).
   */
  cancel(): void;
}

/**
 * Builds an {@link Autosave} over `write`.
 *
 * @param write    performs one save. Its RESOLVED VALUE is the outcome: anything but `false` is
 *                 a success. Both draft backends resolve a boolean and never reject
 *                 (`formApi.saveDraft`, formApi.ts:78-93; `staffFormApi.saveStaffDraft`,
 *                 staffFormApi.ts:57-71), each having already swallowed the network and the
 *                 status ladder, so `false` — not a rejection — is the failure signal on this
 *                 surface. A `write` that rejects or throws synchronously is reported as a
 *                 failure too, and never propagates.
 * @param delayMs  debounce window, defaulting to {@link DRAFT_DEBOUNCE_MS}.
 * @param onStatus notified `saving` before each write and `saved` / `failed` when it settles.
 *                 Optional: with none, auto-save behaves exactly as it did before Phase 425.
 */
export function createAutosave<T>(
  write: (value: T) => Promise<unknown>,
  delayMs: number = DRAFT_DEBOUNCE_MS,
  onStatus?: (status: SaveStatus) => void,
): Autosave<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  /** The value waiting to be written, boxed so that `undefined` can itself be a value. */
  let pending: { value: T } | undefined;
  let inFlight = false;
  /**
   * The settlement of the write currently on the wire — what a {@link Autosave.flush} queues
   * behind. Set and cleared with `inFlight`, so the two never disagree about the wire.
   */
  let flight: Promise<void> | undefined;

  function disarm(): void {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  }

  function arm(): void {
    disarm();
    timer = setTimeout(() => { void fire(); }, delayMs);
  }

  function report(ok: boolean): void {
    onStatus?.(ok ? { kind: "saved", at: Date.now() } : { kind: "failed", at: Date.now() });
  }

  /**
   * Starts the write for the pending value, if there is one and the wire is free. Returns the
   * settlement of the write the caller is now waiting on: the one it just started, the one
   * already in flight, or nothing.
   */
  function fire(): Promise<void> {
    timer = undefined;
    // A write is already on the wire (rule 2). This value is not lost: that write re-arms the
    // timer when it settles (rule 3), and a flush waiting on `flight` gets its turn there.
    if (inFlight) return flight ?? Promise.resolve();
    if (pending === undefined) return Promise.resolve();

    const { value } = pending;
    pending = undefined;
    inFlight = true;
    onStatus?.({ kind: "saving" });
    const settled = Promise.resolve()
      // Inside the chain so that a `write` which throws SYNCHRONOUSLY is caught too.
      .then(() => write(value))
      .then((result) => { report(result !== false); }, () => { report(false); })
      // The original swallow, one link further down the chain: a rejected write has already been
      // reported above, so what this guards now is an `onStatus` observer that throws — which
      // must not wedge the scheduler in flight forever.
      .catch(() => undefined)
      .then(() => {
        inFlight = false;
        flight = undefined;
        if (pending !== undefined) arm();
      });
    flight = settled;
    return settled;
  }

  return {
    schedule(value: T): void {
      pending = { value };
      // While a write is in flight the timer is re-armed on settle rather than now — arming
      // here would only fire into the guard above and burn the debounce window early.
      if (!inFlight) arm();
    },
    flush(value?: T): Promise<void> {
      // `arguments.length`, not `value !== undefined`: `pending` is boxed precisely so that
      // `undefined` can itself be an answer set, and `flush(undefined)` must record one exactly
      // as `schedule(undefined)` does.
      if (arguments.length > 0) pending = { value: value as T };
      // The point of a flush is not waiting out the window.
      disarm();
      if (flight === undefined) return fire();
      // Queued behind the write already on the wire. When it settles it re-arms the debounce for
      // the pending value (rule 3) — disarming that here is what turns "soon" into "now", and
      // taking `pending` in one `fire()` is what keeps it to ONE further write however many
      // changes (or flushes) arrived while we waited.
      return flight.then(() => { disarm(); return fire(); });
    },
    cancel(): void {
      disarm();
      pending = undefined;
    },
  };
}
